import React, { useEffect } from "react";
import { bool } from "prop-types";
import { goBack, push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { ResponsiveHeader } from "pages/_components/header";
import { Field, Form, Formik } from "formik";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import Selector from "pages/_components/fields/formik/Selector";
import FieldLabel from "pages/_components/fields/FieldLabel";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import Loader from "pages/_components/Loader";
import { actions as notificationActions } from "reducers/notification";
import { actions as echeckActions, types } from "reducers/echecks";
import { endorseType } from "util/echecks";
import useSchema from "hooks/useSchema";
import * as i18n from "util/i18n";
import * as Yup from "yup";
import GridTable from "pages/_components/GridTable/GridTable";
import Beneficiaries from "../components/Beneficiaries";
import SummaryActions from "../components/SummaryActions";
import CustomData from "pages/_components/CustomData/CustomData";

const FORM_ID = "echecks.endorse";

const EndorseEcheckPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const { echeqsEndorseData: endorseData, echeqsToDoActions: echeqAction } = useSelector((state) => state.echecks);
    const { stringRequired } = useSchema();
    const hasBeneficiary = endorseData?.beneficiary?.reference;
    const placeholder = i18n.get("global.select");
    const endorseTypeOptions = i18n.formatOption(endorseType, "echecks.endorse.types");

    const echeqList = echeqAction?.echeqList;
    const multipleEcheq = echeqList?.length > 1;

    useEffect(() => {
        if (isEmpty(echeqAction)) {
            dispatch(goBack());
        }
    }, []);

    const initialValues = {
        type: endorseData?.endorseData?.type || "",
        reference: endorseData?.endorseData?.reference || "",
        referencevalue: endorseData?.endorseData?.referencevalue || "",
    };

    const endorseEcheckValidation = Yup.object().shape({
        type: stringRequired,
    });

    const handleSubmit = (values) => {
        if (!endorseData?.beneficiary?.reference) {
            dispatch(
                notificationActions.showNotification(i18n.get("echecks.empty.beneficiary"), "error", [
                    "echecks/endorse",
                ]),
            );
        } else {
            dispatch(echeckActions.endorseEcheqData(values));
            dispatch(push("/echecks/action/endorse/confirm"));
        }
    };

    if (isEmpty(echeqAction)) {
        return <Loader />;
    }

    return (
        <>
            <Notification scopeToShow="echecks/endorse" />
            <Notification scopeToShow="echecks/beneficary" />
            <ResponsiveHeader id={multipleEcheq ? "echecks.multiple.endorse" : "echecks.endorse"} />
            <MainContainer showLoader={false}>
                <div className="container endorse-echeq">
                    <SummaryActions list={echeqList} currency={echeqList[0]?.emisor_moneda} className="endorse-echeq__subtitle" />
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={endorseEcheckValidation}>
                        {({ values }) => (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data
                                            columnStart={2}
                                            columnWidth={10}
                                            flexDirection="column"
                                            className="endorse-echeq__data-benefitiary">
                                            <FieldLabel labelKey="echecks.emit.beneficiary" />
                                            <Beneficiaries
                                                isDesktop={isDesktop}
                                                idForm={FORM_ID}
                                                beneficiaryData={endorseData}
                                                actionType={types.BENEFICIARY_ENDORSE_ECHEQ_DATA}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={5}>
                                            <Field
                                                component={Selector}
                                                idForm={FORM_ID}
                                                name="type"
                                                disabled={!hasBeneficiary}
                                                options={endorseTypeOptions}
                                                placeholder={placeholder}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={5}>
                                            <Field
                                                component={TextField}
                                                labelText="echecks.emit.reference.label"
                                                name="reference"
                                                type="text"
                                                disabled={!hasBeneficiary}
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={2} columnWidth={5}>
                                            <Field
                                                component={TextField}
                                                labelText="echecks.emit.referencevalue.label"
                                                name="referencevalue"
                                                type="text"
                                                disabled={!hasBeneficiary}
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>

                                    </GridTable.Container>
                                </GridTable>
                                <div className="alert alert-info">
                                    <CustomData
                                        icon="information_fill.svg"
                                        titleText="echecks.disclaimer.endorse"
                                        titleTextType="I18N"
                                    />
                                </div>
                                <GridTable>
                                    <GridTable.Container columns={12} columnsTemplate="fixed" className="form-actions">
                                        <GridTable.Data
                                            columnStart={isDesktop ? 5 : 1}
                                            columnWidth={isDesktop ? 4 : 12}>
                                            <Button
                                                type="submit"
                                                className="btn-primary"
                                                label="global.continue"
                                                disabled={!hasBeneficiary || !values.type}
                                            />
                                        </GridTable.Data>
                                    </GridTable.Container>
                                </GridTable>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>
        </>
    );
};

EndorseEcheckPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default EndorseEcheckPage;
